import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ListingCard from '@/components/ListingCard';
import api from '@/lib/api';
import { useTranslation } from '@/contexts/LanguageContext';
import SearchAutocomplete from '@/components/SearchAutocomplete';
import SEO from '@/components/SEO';
import { ListingGridSkeleton } from '@/components/Skeletons';
import { ArrowRight, Upload, ShoppingCart, MessageSquare, Package, BadgeCheck, Layers, Sparkles, Search, Droplet, Wind, Wrench, HardHat, Zap, Trees, Hammer, Handshake } from 'lucide-react';

const BLOG_BACKEND = process.env.REACT_APP_BACKEND_URL;
const blogImg = (src) => (!src ? null : (src.startsWith('http') ? src : `${BLOG_BACKEND}${src}`));

// Secteurs cliquables affichés sous la barre de recherche - basés sur les vrais secteurs DestockPro
const SECTORS_QUICK_NAV = [
  { key: 'piscine', labelFr: 'Piscine', labelNl: 'Zwembad', icon: Droplet },
  { key: 'hvac', labelFr: 'HVAC', labelNl: 'HVAC', icon: Wind },
  { key: 'plomberie', labelFr: 'Plomberie', labelNl: 'Sanitair', icon: Wrench },
  { key: 'construction', labelFr: 'Construction', labelNl: 'Bouw', icon: HardHat },
  { key: 'electricite', labelFr: 'Électricité', labelNl: 'Elektriciteit', icon: Zap },
  { key: 'jardinage', labelFr: 'Jardinage', labelNl: 'Tuinieren', icon: Trees },
  { key: 'outillage', labelFr: 'Outillage', labelNl: 'Gereedschap', icon: Hammer },
  { key: 'autre', labelFr: 'Autre', labelNl: 'Andere', icon: Package },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { t, lang } = useTranslation('home');
  const [searchQuery, setSearchQuery] = useState('');
  const [latestListings, setLatestListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [stats, setStats] = useState({ total_listings: 0, total_sellers: 0, total_sectors: 8 });
  const [loading, setLoading] = useState(true);
  const [publicStats, setPublicStats] = useState({ total_listings: 0, verified: 0 });
  const [blogArticles, setBlogArticles] = useState([]);

  useEffect(() => {
    let retries = 0;
    const fetchHomepage = () => {
      api.get('/homepage').then(r => {
        setFeaturedListings(r.data.featured || []);
        setLatestListings(r.data.latest || []);
        setStats({
          total_listings: r.data.stats?.listings || 0,
          total_sellers: (r.data.stats?.users || 0) + 24,
          total_sectors: 8
        });
        setLoading(false);
      }).catch((err) => {
        console.error('Homepage data fetch failed:', err);
        if (retries < 2) {
          retries++;
          setTimeout(fetchHomepage, 1500 * retries);
        } else {
          setLoading(false);
        }
      });
    };
    fetchHomepage();
    api.get('/stats/public').then(r => {
      setPublicStats({ total_listings: r.data.total_listings || 0, verified: r.data.verified_count || 0 });
    }).catch(() => {});
    api.get('/blog?limit=3').then(r => {
      setBlogArticles(r.data.articles || []);
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/annonces?q=${encodeURIComponent(searchQuery)}`);
  };

  const steps = [
    { icon: Upload, title: t('step1Title'), desc: t('step1Desc') },
    { icon: ShoppingCart, title: t('step2Title'), desc: t('step2Desc') },
    { icon: MessageSquare, title: t('step3Title'), desc: t('step3Desc') },
  ];

  const allListings = [...featuredListings, ...latestListings.filter(l => !featuredListings.find(f => f.id === l.id))].slice(0, 10);

  return (
    <div data-testid="home-page">
      <SEO
        path="/"
        title="Déstockage professionnel en Belgique"
        description="Marketplace B2B de déstockage : achetez et vendez du matériel professionnel neuf, fins de série et surplus, sans commission. Belgique, France, Luxembourg."
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "name": "DestockPro",
              "url": "https://www.destockpro.be",
              "logo": "https://www.destockpro.be/icon-512.png",
              "description": "Marketplace B2B de déstockage de matériel professionnel en Belgique, France, Luxembourg et Pays-Bas.",
              "areaServed": ["BE", "FR", "LU", "NL"],
              "sameAs": ["https://www.linkedin.com/company/destockpro-marketplace"]
            },
            {
              "@type": "WebSite",
              "name": "DestockPro",
              "url": "https://www.destockpro.be",
              "description": "Marketplace B2B de déstockage de matériel professionnel en Belgique",
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://www.destockpro.be/annonces?q={search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
          ]
        }}
      />

      {/* ═══ SECTION 1: Hero ═══ */}
      <section className="bg-[#1F3F72]" data-testid="hero-section">
        <div className="site-container py-10 sm:py-14 lg:py-16 flex flex-col items-center text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" data-testid="hero-title">
            {t('heroTitle')}
          </h1>
          <p className="text-lg sm:text-xl text-white/90 mb-10 max-w-2xl">
            {t('heroSlogan')}
          </p>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="w-full max-w-2xl mb-5" data-testid="hero-search-form">
            <div className="flex gap-0">
              <div className="flex-1 relative">
                <SearchAutocomplete
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSubmit={handleSearch}
                  placeholder={t('searchPlaceholder')}
                  variant="hero"
                  inputClassName="bg-white border-0 text-neutral-900 text-base h-14 rounded-l-xl shadow-lg shadow-black/20"
                />
              </div>
              <Button type="submit" className="h-14 px-6 sm:px-8 bg-[#F25C05] hover:bg-[#d94f04] text-white font-semibold rounded-r-xl rounded-l-none shrink-0 shadow-lg shadow-black/20" data-testid="hero-search-btn">
                <Search className="w-5 h-5 sm:hidden" />
                <span className="hidden sm:inline">{t('searchBtn')}</span>
              </Button>
            </div>
          </form>

          {/* Sector chips */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {SECTORS_QUICK_NAV.map(s => (
              <button
                key={s.key}
                onClick={() => navigate(`/annonces?sector=${encodeURIComponent(s.key)}`)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs sm:text-sm text-white/80 bg-white/10 border border-white/15 hover:bg-white/20 hover:text-white transition-colors"
                data-testid={`sector-${s.key}`}
              >
                <s.icon className="w-3.5 h-3.5" />
                {lang === 'nl' ? s.labelNl : s.labelFr}
              </button>
            ))}
          </div>

          {/* Register link */}
          <Link
            to="/inscription"
            className="text-sm text-white/60 hover:text-white/90 hover:underline transition-colors"
            data-testid="hero-register-link"
          >
            {t('heroRegisterLink')} →
          </Link>
        </div>
      </section>

      {/* ═══ Service Banner ═══ */}
      <section className="bg-slate-800 cursor-pointer" onClick={() => navigate('/service-publication')} data-testid="service-banner">
        <div className="site-container py-5 px-4 sm:py-6 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Handshake className="w-8 h-8 text-[#F25C05] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-white text-sm sm:text-base">{t('serviceBannerTitle')}</p>
              <p className="text-xs sm:text-sm text-slate-300">{t('serviceBannerDesc')}</p>
            </div>
            <Button variant="outline" className="border-[#F25C05] text-[#F25C05] hover:bg-[#F25C05] hover:text-white shrink-0 text-xs sm:text-sm" onClick={(e) => { e.stopPropagation(); navigate('/service-publication'); }} data-testid="service-banner-cta">
              {t('serviceBannerCta')} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2: Listings ═══ */}
      <section className="py-12 lg:py-16 bg-white" data-testid="latest-listings-section">
        <div className="site-container">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900 mb-1">{t('latestListings')}</h2>
              <p className="text-sm sm:text-base text-neutral-500">{t('latestSubtitle')}</p>
            </div>
            <Button variant="outline" className="hidden sm:flex" onClick={() => navigate('/annonces')} data-testid="view-all-listings-btn">
              {t('viewAll')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {loading ? (
            <ListingGridSkeleton count={10} />
          ) : allListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
              {allListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <p className="text-center text-neutral-400 py-12">{t('noListingsYet')}</p>
          )}

          <div className="mt-8 text-center">
            <Button variant="outline" onClick={() => navigate('/annonces')} data-testid="view-all-btn-bottom">
              {t('viewAllListings')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2b: Blog guides ═══ */}
      {blogArticles.length > 0 && (
        <section className="py-12 lg:py-16 bg-neutral-50" data-testid="home-guides-section">
          <div className="site-container">
            <div className="flex items-end justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900 mb-1">{t('blogGuidesTitle')}</h2>
                <p className="text-sm sm:text-base text-neutral-500">{t('blogGuidesSubtitle')}</p>
              </div>
              <Button variant="outline" className="hidden sm:flex" onClick={() => navigate('/blog')} data-testid="home-guides-viewall">
                {t('blogGuidesViewAll')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogArticles.map((a) => {
                const cover = blogImg(a.cover_image);
                return (
                  <Link
                    key={a.id}
                    to={`/blog/${a.slug}`}
                    className="group flex flex-col rounded-xl border border-neutral-100 overflow-hidden bg-white hover:shadow-md transition-all"
                    data-testid={`home-guide-${a.slug}`}
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-[#1F3F72] flex items-center justify-center">
                      {cover ? (
                        <img src={cover} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <Package className="w-8 h-8 text-white/25" />
                      )}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="font-heading text-base font-semibold text-neutral-900 leading-snug mb-2 line-clamp-2 group-hover:text-[#1F3F72] transition-colors">{a.title}</h3>
                      <p className="text-sm text-neutral-500 leading-relaxed line-clamp-3 flex-1">{a.excerpt}</p>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#1F3F72] mt-4">
                        {t('blogGuidesRead')} <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
            <div className="mt-8 text-center sm:hidden">
              <Button variant="outline" onClick={() => navigate('/blog')} data-testid="home-guides-viewall-mobile">
                {t('blogGuidesViewAll')} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ═══ SECTION 3: Trust / Stats ═══ */}
      <section className="py-10 lg:py-14 bg-slate-900" data-testid="trust-section">
        <div className="site-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <div className="text-center">
              <Package className="w-7 h-7 text-[#F25C05] mx-auto mb-2" />
              <p className="text-2xl sm:text-3xl font-bold text-white">{(publicStats.total_listings || stats.total_listings) > 0 ? `${publicStats.total_listings || stats.total_listings}+` : '—'}</p>
              <p className="text-sm text-slate-400 mt-1">{t('statActiveListings')}</p>
            </div>
            <div className="text-center">
              <BadgeCheck className="w-7 h-7 text-[#F25C05] mx-auto mb-2" />
              <p className="text-2xl sm:text-3xl font-bold text-white">{stats.total_sellers > 0 ? `${stats.total_sellers}+` : '—'}</p>
              <p className="text-sm text-slate-400 mt-1">{t('statVerifiedCompanies')}</p>
            </div>
            <div className="text-center">
              <Layers className="w-7 h-7 text-[#F25C05] mx-auto mb-2" />
              <p className="text-2xl sm:text-3xl font-bold text-white">8</p>
              <p className="text-sm text-slate-400 mt-1">{t('statSectors')}</p>
            </div>
            <div className="text-center">
              <Sparkles className="w-7 h-7 text-[#F25C05] mx-auto mb-2" />
              <p className="text-lg sm:text-xl font-semibold text-white leading-tight">{t('statNoCommission')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4: How it works ═══ */}
      <section className="py-12 lg:py-16 bg-neutral-50" data-testid="how-it-works-section">
        <div className="site-container">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900 mb-2">{t('howItWorksTitle')}</h2>
            <p className="text-sm sm:text-base text-neutral-500 max-w-xl mx-auto">{t('howItWorksSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div key={step.title} className="relative bg-white border border-neutral-100 rounded-xl p-6 text-center" data-testid={`step-${i + 1}`}>
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#1F3F72] text-white mb-3">
                  <step.icon className="w-5 h-5" />
                </div>
                <div className="absolute top-3 right-3 text-3xl font-heading font-bold text-neutral-100">{i + 1}</div>
                <h3 className="font-heading text-base font-semibold text-neutral-900 mb-1">{step.title}</h3>
                <p className="text-xs text-neutral-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5: CTA ═══ */}
      <section className="py-12 lg:py-16 bg-[#1F3F72]" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">{t('ctaTitle')}</h2>
          <p className="text-sm sm:text-base text-blue-200 mb-8">{t('ctaText')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button size="lg" className="bg-[#F25C05] hover:bg-[#d94f04] text-white font-semibold" onClick={() => navigate('/inscription')} data-testid="cta-register-btn">
              {t('ctaBtn')}
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 hover:text-white font-semibold" onClick={() => navigate('/annonces')} data-testid="cta-browse-btn">
              {t('ctaBrowse')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
